import { AlertTriangle, ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { type DemandResourceRow, isNosUnavailable, useDemandDetail } from "@/hooks/use-demands";
import { defaultWindowStart, shiftMonth } from "@/lib/demand-window";
import { HttpStatusError } from "@/lib/json-fetch";
import { cn } from "@/lib/utils";
import { MonthBarCell } from "./MonthBarCell";
import { formatMonthColumn, formatMonthShort } from "./month-format";
import { Sparkline } from "./Sparkline";

// Resource · Rate · six months · Trend. Wide enough to read; scrolls sideways when narrow.
const GRID_COLS =
  "grid-cols-[minmax(180px,1.4fr)_96px_repeat(6,minmax(84px,1fr))_96px] min-w-[1000px]";

function formatRate(r: DemandResourceRow): string {
  if (r.rate === null) return "–";
  const amount = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(r.rate);
  return r.rateType ? `${amount} / ${r.rateType.toLowerCase()}` : amount;
}

/**
 * The expanded view of one Demand: its Resources × a six-month window (spec R4–R7,
 * R9–R11). Opens on the window ending at the current month; arrows step one month within
 * the Demand's data span, and Today returns to the opening window.
 */
export function DemandResourceGrid({ demandId }: { demandId: string }) {
  // null = the server default (current month in the last column).
  const [windowStart, setWindowStart] = useState<string | null>(null);
  const { data, isLoading, isFetching, error, refetch } = useDemandDetail(
    demandId,
    windowStart,
    true,
  );

  if (isLoading) {
    return (
      <div className="space-y-2 p-4">
        <Skeleton className="h-6 w-64" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (error || !data) {
    // Outside the reader's Account scope (viewReportingAnalytics), or no longer active in NOS.
    if (error instanceof HttpStatusError && error.status === 404) {
      return (
        <p className="p-4 text-sm text-muted-foreground">
          This NOS Demand isn't available here. It may be closed in NOS, or outside your Accounts.
        </p>
      );
    }
    if (isNosUnavailable(error)) {
      return (
        <div className="m-4 flex items-start gap-2 rounded-md border border-warning-200 bg-warning-50 px-3 py-2 text-sm text-warning-950">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <span>
            NOS isn't available right now, so this Demand's Resources can't be shown. (
            {error?.message})
          </span>
        </div>
      );
    }
    return (
      <div className="p-4">
        <ErrorState
          message={error?.message ?? "Failed to load this Demand."}
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  const start = data.windowStart;
  const end = data.months[data.months.length - 1]?.month ?? start;
  const atDefault = windowStart === null || start === defaultWindowStart(new Date());
  const maxHours = Math.max(
    1,
    ...data.resources.flatMap((r) =>
      r.cells.flatMap((c) => [c.actualHours ?? 0, c.guidanceHours ?? 0]),
    ),
  );
  const windowHasData = data.resources.some((r) =>
    r.cells.some((c) => c.actualHours !== null || c.guidanceHours !== null),
  );

  return (
    <div className={cn("space-y-3 p-4 transition-opacity", isFetching && "opacity-70")}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            aria-label="Previous month"
            disabled={!data.canStepBack}
            onClick={() => setWindowStart(shiftMonth(start, -1))}
          >
            <ChevronLeft className="size-4" />
          </Button>
          <span className="min-w-40 text-center font-mono text-xs text-muted-foreground">
            {formatMonthShort(start)} – {formatMonthShort(end)}
          </span>
          <Button
            variant="outline"
            size="sm"
            aria-label="Next month"
            disabled={!data.canStepForward}
            onClick={() => setWindowStart(shiftMonth(start, 1))}
          >
            <ChevronRight className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={atDefault}
            onClick={() => setWindowStart(null)}
          >
            Today
          </Button>
        </div>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-3 w-1.5 rounded-sm bg-brand-600" /> Actual (closed month)
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-0.5 w-3 bg-foreground/70" /> Planned
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-3 w-1.5 rounded-sm bg-info-300" /> Guidance (open month)
          </span>
        </div>
      </div>

      {data.nosCurrentMonth && (
        <p className="text-xs text-muted-foreground">
          NOS is still on {formatMonthShort(data.nosCurrentMonth)}. Months show as actuals only once
          NOS closes them.
        </p>
      )}

      {data.resources.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          No Resources are on this Demand in NOS.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <div
            className={cn(
              "grid items-end gap-3 border-b border-border bg-secondary px-3 py-2",
              GRID_COLS,
            )}
          >
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Resource
            </span>
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Rate
            </span>
            {data.months.map((m, i) => (
              <span
                key={m.month}
                className={cn("rounded-sm px-1 leading-tight", m.isCurrent && "bg-brand-50")}
              >
                <span className="block text-xs font-bold uppercase tracking-wider text-foreground">
                  {formatMonthColumn(m.month, i === 0)}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {m.isClosed === null ? "–" : m.isClosed ? "Actual" : "Guidance"}
                </span>
              </span>
            ))}
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Trend
            </span>
          </div>
          <div className="divide-y divide-border">
            {data.resources.map((r) => (
              <div
                key={r.demandPersonId}
                className={cn(
                  "grid items-center gap-3 px-3 py-2",
                  GRID_COLS,
                  !r.hasData && "opacity-60",
                )}
              >
                <div className="min-w-0">
                  <div className="truncate font-display text-sm font-semibold leading-tight">
                    {r.name}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {[r.role, r.status].filter(Boolean).join(" · ") || "—"}
                  </div>
                </div>
                <div className="font-mono text-xs tabular-nums">{formatRate(r)}</div>
                {r.cells.map((c, i) => (
                  <div
                    key={c.month}
                    className={cn("rounded-sm px-1", data.months[i]?.isCurrent && "bg-brand-50/60")}
                  >
                    <MonthBarCell
                      cell={c}
                      isClosed={data.months[i]?.isClosed === true}
                      maxHours={maxHours}
                    />
                  </div>
                ))}
                <Sparkline
                  label={`${r.name} hours trend, ${formatMonthShort(start)} to ${formatMonthShort(end)}`}
                  values={r.cells.map((c, i) =>
                    data.months[i]?.isClosed === true ? c.actualHours : c.guidanceHours,
                  )}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {!windowHasData && data.resources.length > 0 && (
        <div className="flex items-center justify-center gap-3 text-sm text-muted-foreground">
          <span>No guidance or actuals in these months.</span>
          {data.lastActivityStart && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setWindowStart(data.lastActivityStart)}
            >
              Jump to last activity
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
